import { Fragment } from 'react'
import { useDndContext } from '@dnd-kit/core'
import classNames from 'classnames'

import { isFolderFile } from '../../../utils/file'
import { toCSSPixel } from '../../../utils/styles'
import {
  DEFAULT_DRAG_STYLE,
  DragDropDraggableItem,
  DragDropDroppableItem,
  DRAGGING_STYLE,
  NO_DRAG_STYLE,
  NO_DROP_STYLE,
} from '../../DragDrop'
import {
  getTableRowQaProps,
  TableRowActions,
  TableRowContextActions,
  TableRowLoader,
  useTableRowActions,
  useTableRowModClassName,
} from '../../Table'

import { ACTIVE_DRAGGING_STYLE } from './DocumentTableRow.constants'
import { renderCell } from './DocumentTableRow.utils'

import * as styles from './DocumentTableRow.module.css'

function DocumentTableRow({
  Actions = TableRowActions,
  actionLeft,
  actionsColumnWidth,
  columns,
  emptyColumnSize,
  focusedRow,
  getActions,
  getDraggableRowProps,
  hideCheckbox,
  hoveredId,
  isResizing,
  isSelected,
  isShort,
  item,
  resizingColumn,
  scrollRef,
  showCellBorder,
  showSymbols,
  onClick,
  onClickFolder,
  ...restProps
}) {
  const { uploading } = item

  const {
    handleCallAction,
    handleMouseDown,
    handleMouseEnter,
    handleMouseLeave,
    handleSelect,
  } = useTableRowActions({ item, ...restProps })

  const qaProps = getTableRowQaProps()

  function handleClick(event) {
    if (isFolderFile(item)) {
      onClickFolder?.(item, event)
    } else {
      onClick?.(item.id, event)
    }
  }

  const isHoveredItem = Number(hoveredId) === item.id

  const cellClassName = classNames(styles.DocumentTableCell, {
    [styles.uploading]: uploading,
  })

  const className = useTableRowModClassName({
    className: styles.DocumentTableRow,
    isDragHover: isHoveredItem,
    isSelected,
  })

  const { active } = useDndContext()

  const isDndActive = Boolean(active)

  const isForbiddenFolder = isFolderFile(item) && !item?.permissions?.can_write

  const draggableDisabled = !item?.permissions?.can_move || uploading

  function getItemStyle() {
    if (isDndActive) {
      return isForbiddenFolder ? NO_DROP_STYLE : DRAGGING_STYLE
    } else if (isForbiddenFolder) {
      return NO_DRAG_STYLE
    }

    return DEFAULT_DRAG_STYLE
  }

  return (
    <DragDropDroppableItem
      className={styles.DroppableItem}
      id={item.id.toString()}
    >
      <DragDropDraggableItem
        {...qaProps}
        {...getDraggableRowProps(item)}
        keepInPlace
        className={className}
        draggableProps={{
          data: {
            item,
          },
          disabled: draggableDisabled,
        }}
        draggingStyle={ACTIVE_DRAGGING_STYLE}
        id={item.id.toString()}
        style={getItemStyle()}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {Actions ? (
          <Actions
            disabled={hideCheckbox || uploading}
            selected={isSelected}
            width={actionsColumnWidth}
            onChange={handleSelect}
          />
        ) : null}
        {columns.map(column => (
          <Fragment key={column.name}>
            {renderCell({
              className: cellClassName,
              disabled: uploading,
              isActive: column.name === resizingColumn,
              isResizing,
              isShort,
              item,
              name: column.name,
              scrollRef,
              showCellBorder,
              showSymbols,
              textClassName: styles.cellText,
              width: column.width,
            })}
          </Fragment>
        ))}
        {emptyColumnSize ? (
          <div style={{ width: toCSSPixel(emptyColumnSize) }} />
        ) : null}
        {!uploading && getActions?.(item).length > 0 ? (
          <TableRowContextActions
            actionLeft={actionLeft}
            focusedRow={focusedRow}
            getActions={getActions}
            item={item}
            onCallAction={handleCallAction}
          />
        ) : null}
        {uploading ? <TableRowLoader /> : null}
      </DragDropDraggableItem>
    </DragDropDroppableItem>
  )
}

export default DocumentTableRow
